import { useCallback, useEffect, useRef, useState } from 'react';
import { getControlledQueue } from '../../../api/pharmacy';
import useRefreshOnFocus from '../../../hooks/useRefreshOnFocus';
import { toInventoryList } from '../../../api/inventory-request';

function rowLineId(row) {
  return String(row?.inventory_transaction_item_id || row?.transaction_item_id || '');
}

/**
 * Read the controlled-drug authorizations raised against ONE draft sale.
 *
 * Deliberately has no timer. The counter parks a controlled order and walks
 * away — it is not supposed to stand here watching, so there is nothing to poll
 * for. This refreshes on mount, when the window regains focus (i.e. the user
 * comes back from the pharmacist's tab), and on demand. Anything longer-lived
 * belongs on the Controlled Orders screen.
 */
export default function useControlledAuthQueue({ saleId, scope, enabled }) {
  const [authByLineId, setAuthByLineId] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const reload = useCallback(async () => {
    if (!saleId) return {};
    setLoading(true);
    try {
      const payload = await getControlledQueue({
        ...(scopeRef.current || {}),
        inventory_transaction_id: saleId,
        // No status filter: an AUTHORIZED or DENIED row is exactly what the
        // counter needs to see.
        status: undefined,
      });
      const next = {};
      toInventoryList(payload).forEach((row) => {
        // Re-filter by sale even though the query already asked for it. Line
        // ids are NOT unique across transactions in existing data — older
        // clients wrote a stock-balance id into inventory_transaction_item_id,
        // so the same key appears on rows from many sales. Trusting the key
        // alone would let another sale's AUTHORIZED row unlock this line.
        if (String(row?.inventory_transaction_id || '') !== String(saleId)) return;
        const lineId = rowLineId(row);
        if (lineId) next[lineId] = row;
      });
      setAuthByLineId(next);
      setError(null);
      setLastCheckedAt(new Date().toISOString());
      return next;
    } catch (err) {
      setError(err?.message || 'Could not read the controlled queue');
      return {};
    } finally {
      setLoading(false);
    }
  }, [saleId]);

  useEffect(() => {
    if (!enabled || !saleId) {
      setAuthByLineId({});
      return;
    }
    reload();
  }, [enabled, saleId, reload]);

  useRefreshOnFocus(reload, Boolean(enabled && saleId));

  return { authByLineId, loading, error, lastCheckedAt, reload };
}
